import React, { useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";

// A horizontal scrollbar that sits above what it scrolls.
//
// A wide table's own scrollbar lives at the bottom, which on a long list means
// scrolling to the end of the page to discover there were more columns all
// along. This mirrors the target's scroll position in a thin rail placed above
// it, and renders nothing at all when everything already fits — so it only
// appears on the screens that actually need it.
export default function TopScrollbar({ targetRef, watch }) {
  const railRef = useRef(null);
  // 0 = nothing overflows, so there is nothing to show.
  const [width, setWidth] = useState(0);
  // Guards the two scroll handlers from answering each other.
  const syncing = useRef(false);

  useEffect(() => {
    const target = targetRef.current;
    if (!target) return undefined;
    // A few pixels of slack: sub-pixel column rounding routinely leaves 1-3px
    // of "overflow" that nobody can scroll to and that would show a rail for
    // nothing.
    const measure = () =>
      setWidth(target.scrollWidth > target.clientWidth + 4 ? target.scrollWidth : 0);
    measure();
    // The table's width changes with the viewport and with the rows that arrive,
    // and jsdom has no ResizeObserver, hence the window fallback.
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }
    const observer = new ResizeObserver(measure);
    observer.observe(target);
    if (target.firstElementChild) observer.observe(target.firstElementChild);
    return () => observer.disconnect();
  }, [targetRef, watch]);

  useEffect(() => {
    const target = targetRef.current;
    const rail = railRef.current;
    if (!target || !rail || !width) return undefined;
    const follow = (from, to) => () => {
      if (syncing.current) return;
      syncing.current = true;
      to.scrollLeft = from.scrollLeft;
      syncing.current = false;
    };
    const fromRail = follow(rail, target);
    const fromTarget = follow(target, rail);
    rail.addEventListener("scroll", fromRail, { passive: true });
    target.addEventListener("scroll", fromTarget, { passive: true });
    return () => {
      rail.removeEventListener("scroll", fromRail);
      target.removeEventListener("scroll", fromTarget);
    };
  }, [width, targetRef]);

  if (!width) return null;

  return (
    <Box
      ref={railRef}
      aria-hidden
      sx={{
        overflowX: "auto",
        overflowY: "hidden",
        // Touch devices scroll the table directly and hide their scrollbars, so
        // the rail would be an invisible dead strip; keep it off the layout.
        display: { xs: "none", md: "block" },
      }}
    >
      <Box sx={{ width, height: "1px" }} />
    </Box>
  );
}
